import {
  Component,
  EnvironmentInjector,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { AuthService } from '@aid/auth/services';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { User } from '@aid/core/types/classes';
import { SidenavOption } from '@aid/core/types/interfaces';
import {
  AppModeService,
  CoreSidenavService,
  IdleService,
  SidenavService,
  UserService
} from '../../services';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { OrganizationService } from '@aid/core/services/organization.service';
import { Organization } from '@aid/core/types/classes/organization.class';
import { filter, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { Reminder } from '@aid/reminders/types/classes';
import { RemindersService } from '@aid/reminders/services';
import { OrganizationMember } from '@aid/core/types/classes/organization-member.class';
import { NotificationsService } from '@aid/notifications/services';
import { NotificationsComponent } from '@aid/notifications/containers';
import { MembersService } from '@aid/members/services';
import { SubscriptionStatus } from '@aid/subscriptions/types/enums';
import { MatDialog } from '@angular/material/dialog';
import { SessionTimeoutComponent } from '@aid/core/components/session-timeout/session-timeout.component';

@Component({
  selector: 'aid-main-layout',
  template: `
    <aid-right-sidenav>
      <aid-app-bar
        [organization]="organization$ | async"
        [reminders]="currentReminders"
        [currentMember]="currentMember$ | async"
        [user]="user$ | async"
        [notificationBadge]="notificationBadge$ | async"
        [reminderBadge]="reminderBadge$ | async"
        [customerMode]="customerMode$ | async"
        (loadReminders)="onLoadReminders()"
        (viewNotifications)="onViewNotifications()"
        (viewProfile)="onViewProfile()"
        (toggleCustomerMode)="onToggleMode()"
      ></aid-app-bar>
      <div
        class="main-layout--color full-width full-height overflow aid-layout-row"
      >
        <aid-sidenav
          [sidenavOptions]="sidenavOptions"
          [active]="active"
          [user]="user$ | async"
          [collapsed]="sidenavCollapsed$ | async"
          (collapse)="onCollapse()"
        ></aid-sidenav>

        <div
          class="main-layout__container full-width full-height flex overflow aid-layout-column"
        >
          @if (subscriptionExpiring) {
            <div class="padding-horizontal-32 padding-top-16 aid-layout-column">
              <aid-warning-toast
                [message]="'subscriptions.expiration-message'"
                [title]="'subscriptions.expiration-title'"
                (close)="onCloseWarning()"
              >
              </aid-warning-toast>
            </div>
          }

          <section class="main-layout__content full-height flex">
            <div class="flex full-width full-height overflow">
              <div class="flex-1">
                <router-outlet></router-outlet>
              </div>
            </div>
          </section>
        </div>
      </div>
    </aid-right-sidenav>
  `,
  styleUrls: ['./main-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  currentRoute$?: BehaviorSubject<ActivatedRouteSnapshot>;
  user$?: Observable<User | null>;
  active = '';
  sidenavOptions: SidenavOption[] = [];
  tabBarTitle?: string;
  organization$?: Observable<Organization | null>;
  sidenavCollapsed$?: Observable<boolean | null>;
  customerMode$?: Observable<boolean | null>;
  subscriptionExpiring = false;

  currentMember$?: Observable<OrganizationMember | null>;
  currentReminders?: Reminder[];
  notificationBadge$?: Observable<number>;
  reminderBadge$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private ngUnsubscribe = new Subject<void>();

  private readonly userService = inject(UserService);
  private readonly organizationService = inject(OrganizationService);
  private readonly membersService = inject(MembersService);
  private readonly authService = inject(AuthService);
  private readonly sidenavService = inject(SidenavService);
  private readonly router = inject(Router);
  private readonly remindersService = inject(RemindersService);
  private readonly coreSidenavService = inject(CoreSidenavService);
  private readonly notificationService = inject(NotificationsService);
  private readonly appModeService = inject(AppModeService);
  private readonly idleService = inject(IdleService);
  private readonly matDialog = inject(MatDialog);
  private readonly environmentInjector = inject(EnvironmentInjector);

  ngOnInit() {
    this.authService.setSharedLoading(false);

    this.membersService.onRefresh();
    this.coreSidenavService.initFromLocalStorage();
    this.sidenavCollapsed$ = this.coreSidenavService.collapsed$;

    this.appModeService.initFromLocalStorage();
    this.customerMode$ = this.appModeService.customerMode$;

    this.user$ = this.userService.user$;

    this.organization$ = this.organizationService.organization$;
    this.sidenavOptions = this.sidenavService.buildSidenavOptions(
      this.organizationService.requireOrganization()
    );

    this.setAppBarTitle();

    const currentRoute$ = new BehaviorSubject(
      this.router.routerState.root.snapshot
    );
    this.currentRoute$ = currentRoute$;
    this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.setAppBarTitle();
        currentRoute$.next(this.router.routerState.root.snapshot);
      }
    });

    this.currentMember$ = this.organizationService.organizationMember$;

    this.subscribeNotifications();
    this.notificationBadge$ = this.notificationService.subscribeBadge$();
    this.remindersService
      .getBadge$()
      .subscribe((badge: number) => this.reminderBadge$.next(badge));

    this.checkSubscriptionExpiring();
    this.checkUserInactivity();
  }

  onLoadReminders() {
    this.remindersService
      .currentReminders()
      .subscribe((reminders: Reminder[]) => {
        const badge = reminders.filter(
          reminder =>
            new Date(reminder.dueDate as Date).getDay() === new Date().getDay()
        ).length;
        if (badge >= 0) {
          this.reminderBadge$.next(badge);
          this.remindersService.setBadge(badge);
        }
        this.currentReminders = reminders;
      });
  }

  onCollapse() {
    this.coreSidenavService.toggle();
  }

  onToggleMode() {
    this.appModeService.toggle();
  }

  onViewNotifications() {
    this.sidenavService.open(
      NotificationsComponent,
      this.environmentInjector,
      600
    );

    this.notificationService.seenAll().subscribe();
  }

  onViewProfile() {
    this.router.navigate([
      this.organizationService.requireOrganization().slug,
      'profile'
    ]);
  }

  private checkSubscriptionExpiring() {
    this.subscriptionExpiring =
      this.organizationService.requireOrganization().subscriptionStatus ===
      SubscriptionStatus.EXPIRING;
  }

  onCloseWarning() {
    this.subscriptionExpiring = false;
  }

  private subscribeNotifications() {
    this.notificationService
      .subscribeLatest$()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(() => !this.sidenavService.isOpenedValue)
      )
      .subscribe(value => this.notificationService.show(value));
  }

  private setAppBarTitle() {
    const url = this.router.url;
    this.active = `${this.organizationService.requireOrganization().slug}/${
      url.split('/')[2] || ''
    }`.split('?')[0];
    const option = this.sidenavOptions.find(
      value => value.routerLink === this.active
    );
    this.tabBarTitle = option ? option.name : 'dashboard';
  }

  private checkUserInactivity() {
    this.idleService
      .startWatching(1200)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();

    this.idleService
      .checkIdle()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged(),
        filter(value => !!value)
      )
      .subscribe(() => this.logOut());
  }

  private logOut() {
    this.userService.logOut();
    this.matDialog.closeAll();
    this.sidenavService.close();

    this.router.navigate(['auth'], {
      queryParams: {
        redirectTo: document.location.pathname
      }
    });

    this.openSessionTimeout();
  }

  private openSessionTimeout() {
    this.matDialog.open(SessionTimeoutComponent, {
      width: '500px'
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
